<template>
  <div
    class="dealership-select"
    :class="{ 'dealership-select--fluid': fluid }"
  >
    <Select
      :id="id"
      :model-value="modelValue"
      :options="groupedOptions"
      :mode="mode"
      :chips="chips"
      :chips-displayed="chipsDisplayed"
      :placeholder="resolvedPlaceholder"
      :size="size"
      :readonly="readonly"
      :clearable="clearable"
      :teleport="teleport"
      :has-error="hasError"
      :icon="icon"
      :alt="alt"
      :auto-expand="autoExpand"
      :auto-hide-icon="autoHideIcon"
      :dropdown-match-width="dropdownMatchWidth"
      :can-remove-chips="canRemoveChips"
      :dropdown-max-height="dropdownMaxHeight"
      searchable
      option-group-label="groupLabel"
      option-group-children="dealerships"
      value-key="value"
      label-key="label"
      @update:model-value="onUpdateModelValue"
      @select="$emit('select', $event)"
      @deselect="$emit('deselect', $event)"
      @change="$emit('change', $event)"
    >
    <template #dropdownHeader>
      <div
        class="dealership-select-group-by"
        role="tablist"
        :aria-label="t('ui.common.groupBy')"
        @mousedown.stop
        @click.stop
      >
        <button
          v-for="choice in groupByChoices"
          :key="choice.value"
          type="button"
          role="tab"
          class="dealership-select-group-by__option"
          :class="{ 'is-active': groupBy === choice.value }"
          :aria-selected="groupBy === choice.value"
          @click="setGroupBy(choice.value)"
        >
          {{ choice.label }}
        </button>
      </div>
    </template>
  </Select>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

// components
import Select from '@/components/forms/Select.vue'
// composables
import { useDealershipSelectOptions } from '@/composables/useDealershipSelectOptions'
// types
import type { DealershipSelectGroupBy } from '@/types/domain/dealership'
// utilities
import { groupDealershipsForSelect } from '@/utilities/dealershipSelect'

/** localStorage key for the last grouping mode the user picked */
const GROUP_BY_STORAGE_KEY = 'dealership-select-group-by'

const props = withDefaults(
  defineProps<{
    /** Selected dealership ID (single) or IDs (multiple) */
    modelValue?: string | number | object | unknown[] | null
    /** Selection mode forwarded to Select */
    mode?: 'single' | 'multiple'
    /** Show selected values as chips */
    chips?: boolean
    /** Max chips shown before overflow */
    chipsDisplayed?: number
    /** Trigger placeholder */
    placeholder?: string
    /** Form control size */
    size?: string
    /** Prevent opening the dropdown */
    readonly?: boolean
    /** Show a clear control on the trigger */
    clearable?: boolean
    /** Teleport the dropdown to the overlay root */
    teleport?: boolean
    /** Error state on the trigger */
    hasError?: boolean
    /** Native id on the trigger */
    id?: string | null
    /** Optional leading icon */
    icon?: string | null
    /** Accessible label for the trigger */
    alt?: string | null
    /** Grow the trigger with chips */
    autoExpand?: boolean
    /** Hide the icon once a value is selected */
    autoHideIcon?: boolean
    /** Match dropdown width to the trigger */
    dropdownMatchWidth?: boolean
    /** Allow removing chips from the trigger */
    canRemoveChips?: boolean
    /** Max height of the dropdown panel */
    dropdownMaxHeight?: string
    /** Limit options to these IDs; defaults to the user's allowed dealerships */
    dealershipIds?: number[]
    /** Grouping used when nothing is stored yet */
    defaultGroupBy?: DealershipSelectGroupBy
    /** Persist grouping across pages */
    persistGroupBy?: boolean
    /** Fill the parent instead of using the header width */
    fluid?: boolean
  }>(),
  {
    modelValue: null,
    mode: 'multiple',
    chips: true,
    chipsDisplayed: 3,
    placeholder: undefined,
    size: 'md',
    readonly: false,
    clearable: false,
    teleport: true,
    hasError: false,
    id: null,
    icon: null,
    alt: null,
    autoExpand: false,
    autoHideIcon: false,
    dropdownMatchWidth: true,
    canRemoveChips: false,
    dropdownMaxHeight: '360px',
    dealershipIds: undefined,
    defaultGroupBy: 'manufacturer',
    persistGroupBy: true,
    fluid: false
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: unknown]
  select: [value: unknown]
  deselect: [value: unknown]
  change: [value: unknown]
}>()

const { t } = useI18n()
const dealershipIds = computed(() => props.dealershipIds)
const { options, hasTags, isLoading } = useDealershipSelectOptions(dealershipIds)

/** Active grouping for the dropdown list */
const groupBy = ref<DealershipSelectGroupBy>(readStoredGroupBy() ?? props.defaultGroupBy)

/** Placeholder shown when nothing is selected */
const resolvedPlaceholder = computed(() => props.placeholder ?? t('ui.entities.dealership', 1))

/** Group-by modes shown in the dropdown toolbar */
const groupByChoices = computed(() => {
  const choices: Array<{ value: DealershipSelectGroupBy; label: string }> = [
    { value: 'manufacturer', label: t('ui.entities.brand', 1) },
    { value: 'org', label: t('ui.entities.group', 1) }
  ]

  // Tag grouping is only useful once at least one dealership has tags
  if (hasTags.value || groupBy.value === 'tag') {
    choices.push({ value: 'tag', label: t('ui.common.tag', 1) })
  }

  return choices
})

/** Options grouped for the shared Select */
const groupedOptions = computed(() =>
  groupDealershipsForSelect(options.value, groupBy.value, {
    other: t('ui.common.other'),
    untagged: t('ui.common.untagged')
  })
)

// Fall back when grouping is Tag but the loaded list has none (avoid clobbering during fetch)
watch([hasTags, isLoading], ([hasAnyTags, loading]) => {
  if (!loading && !hasAnyTags && groupBy.value === 'tag') {
    setGroupBy(props.defaultGroupBy === 'tag' ? 'manufacturer' : props.defaultGroupBy)
  }
})

/**
 * Read the last grouping mode from localStorage.
 *
 * @returns A valid grouping mode, or null when nothing usable is stored
 */
function readStoredGroupBy(): DealershipSelectGroupBy | null {
  if (!props.persistGroupBy) {
    return null
  }

  try {
    const stored = localStorage.getItem(GROUP_BY_STORAGE_KEY)
    return isGroupBy(stored) ? stored : null
  } catch {
    return null
  }
}

/**
 * Check that a value is a supported grouping mode.
 *
 * @param value - Candidate grouping value
 * @returns True when the value can be used as groupBy
 */
function isGroupBy(value: unknown): value is DealershipSelectGroupBy {
  return value === 'manufacturer' || value === 'org' || value === 'tag'
}

/**
 * Apply a grouping mode and optionally persist it for other pages.
 *
 * @param value - Grouping the user picked
 */
function setGroupBy(value: DealershipSelectGroupBy): void {
  groupBy.value = value

  if (!props.persistGroupBy) {
    return
  }

  try {
    localStorage.setItem(GROUP_BY_STORAGE_KEY, value)
  } catch {
    // Private mode / quota errors should not break selection
  }
}

/**
 * Forward v-model updates from Select.
 *
 * @param value - New selected dealership ID or IDs
 */
function onUpdateModelValue(value: unknown): void {
  emit('update:modelValue', value)
}
</script>

<style scoped>
.dealership-select {
  width: 100%;
}

/* Fixed width in page headers so selections cannot shrink or stretch the control */
.dealership-select:not(.dealership-select--fluid) {
  width: 16rem;
  max-width: 16rem;
  flex-shrink: 0;
}

@media (min-width: 1024px) {
  .dealership-select:not(.dealership-select--fluid) {
    width: 20rem;
    max-width: 20rem;
  }
}

.dealership-select :deep(.select-wrapper) {
  width: 100%;
}

.dealership-select-group-by {
  display: flex;
  gap: 2px;
  margin: var(--s-1) var(--s-2) var(--s-2);
  padding: 2px;
  background: var(--bg-secondary_alt);
  border: 1px solid var(--border-secondary);
  border-radius: var(--rounded-lg);
}

.dealership-select-group-by__option {
  flex: 1;
  border: 0;
  background: transparent;
  color: var(--text-tertiary);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  line-height: var(--text-sm-lh);
  padding: var(--s-1) var(--s-2);
  border-radius: var(--rounded-md);
  cursor: pointer;
  white-space: nowrap;
}

.dealership-select-group-by__option.is-active {
  background: var(--bg-primary_alt);
  color: var(--text-secondary);
  box-shadow: var(--shadow-sm);
}
</style>
